import { EWFAssignmentType, IWFNodeAssignment, IWFStep } from "@kuika/kuika-cl-model";
import { IWFNodes } from "@kuika/kuika-cl-model/src/models/workflow";

/**
 * Read-repair for `useLaneAssignment` on deserialized workflow steps.
 *
 * `IWFStep.useLaneAssignment` is typed non-optional `boolean`, but workflows saved by older
 * builds (or paths that wrote the assignment object without the flag) deserialize it as
 * `undefined`. The step properties panel derives `disabled` as
 * `useLaneAssignment !== undefined ? useLaneAssignment : true`, so an `undefined` flag coerces
 * to "use lane assignment" and the whole Dynamic/Role assignment sub-panel is hidden — even
 * though the step still carries `assignmentType: dynamic` and a `dynamicAssignmentAction`. The
 * user sees "Dynamic" stuck in the type dropdown with no way to view or edit the action.
 *
 * A blanket default is unsafe: the platform default for new steps is `true`, so collapsing every
 * `undefined` to `false` would silently detach genuinely lane-inheriting legacy steps (changing
 * generated/runtime behavior). Instead, only steps that carry a step-level assignment of their own
 * (a dynamic action, a role parameter, or an external choice) are repaired to `false` (editable);
 * the rest keep the platform default `true`. Recurses into subprocess steps. Mutates in place.
 */
export function normalizeStepLaneAssignments(nodes: IWFNodes | undefined): void {
  if (!nodes?.steps) return;
  nodes.steps.forEach((step: IWFStep) => {
    if (typeof step.useLaneAssignment !== "boolean") {
      step.useLaneAssignment = !stepHasOwnAssignment(step.assignment);
    }
    if (step.subprocess) {
      normalizeStepLaneAssignments(step.subprocess);
    }
  });
}

/**
 * True when an assignment represents a deliberate step-level choice rather than a trivial
 * lane-inheritable default. A dynamic action, a role parameter, or the external type are all
 * concrete configurations a lane-inheriting step would not carry on its own.
 */
function stepHasOwnAssignment(assignment: IWFNodeAssignment | undefined): boolean {
  if (!assignment) return false;
  return (
    !!assignment.dynamicAssignmentAction ||
    !!assignment.roleAssignmentParameter ||
    assignment.assignmentType === EWFAssignmentType.external
  );
}
